import React from 'react'
import {
  Area,
  AreaChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'

import { Body2, Title1 } from '@ui/Typography'
import { CustomChartTooltip } from '@ui/components/Chart/CustomChartTooltip'
import { Tooltip } from '@ui/components/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'

import { getCurrencySymbol } from '@utils/transaction.utils'

import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import styles from './StatCard.module.scss'

const PLACEHOLDER_CHART_DATA: ChartDataPoint[] = [
  { name: '', value: 0 },
  { name: '', value: 0 },
]

interface ChartDataPoint {
  value: number
  name: string
  tooltipLabel?: string
}

interface StatCardProps {
  id?: string
  title: string
  value: number
  chartData?: {
    gradientId: string
    data: ChartDataPoint[]
  }
  unit?: CurrencyType
  shouldRenderChart?: boolean
  tooltip?: string
}

export const StatCard = ({
  title,
  value,
  chartData,
  unit,
  shouldRenderChart = true,
  tooltip,
}: StatCardProps) => {
  const { formatNumber } = useFormatNumber()

  const isDataPresent = !!(chartData && chartData.data.length) && value > 0
  const color = isDataPresent ? 'var(--outline-warning-high-em)' : 'var(--text-med-em)'
  const gradientId = chartData?.gradientId
  const data = isDataPresent ? chartData.data : PLACEHOLDER_CHART_DATA
  const formattedValue = formatNumber(value, { minDecimalPlaces: 0 })

  return (
    <Box
      className={styles.statCard}
      fullWidth
      radius='x20'
      border='outline-low-em'
      bg='surface-white-smoke-0'
      p='x8'
      gap='x12'
    >
      <Box flex direction='column' p='x8' gap='x4'>
        <Box flex gap='x4' align='center'>
          <Body2 color='text-med-em'>{title}</Body2>
          {tooltip && (
            <Tooltip content={tooltip}>
              <InfoIcon size={18} />
            </Tooltip>
          )}
        </Box>
        <Title1 semibold color='text-high-em'>
          {unit ? `${getCurrencySymbol(unit)} ${formattedValue}` : formattedValue}
        </Title1>
      </Box>
      {shouldRenderChart ? (
        <Box className={styles.chartContainer}>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={data} margin={{ top: 0, right: 8, left: 8, bottom: 8 }}>
              <defs>
                <linearGradient id={`${gradientId}-gradient`} x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={color} stopOpacity={0.7} />
                  <stop offset='50%' stopColor={color} stopOpacity={0.6} />
                  <stop offset='100%' stopColor={color} stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <XAxis dataKey='name' hide />
              <YAxis hide />
              {data ? (
                <RechartsTooltip
                  position={{ y: -60 }}
                  cursor={false}
                  content={(props: TooltipProps<number, string>) => (
                    <CustomChartTooltip
                      {...props}
                      labelFormatter={(label) => {
                        const dataPoint = data?.find((d) => d.name === label)
                        return dataPoint?.tooltipLabel || label
                      }}
                      unit={unit}
                    />
                  )}
                />
              ) : null}
              <Area
                type='monotone'
                dataKey='value'
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradientId}-gradient)`}
                dot={false}
                activeDot={{
                  r: 6,
                  fill: 'white',
                  stroke: color,
                  strokeWidth: 3,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      ) : null}
    </Box>
  )
}
